/**
 * The one thing that has to survive an off-site authorisation: which card the merchant left from.
 *
 * <p><b>Why any of this exists.</b> Reconnect and Top up are the only two actions that take the
 * merchant out of the panel, and both finish somewhere we do not control — Shopify's OAuth screen,
 * the billing page. Nothing calls us back. So the browser coming back IS the signal that the cause
 * was fixed, and the server says as much: it puts the id to post inside the navigate action and
 * documents it as 「posted by the RETURN, not by a button」. Until this module existed that id was
 * read by nobody, and V0.9's 「完成后自动回任务中心并重开面板，该卡 ✓ 收起」 simply did not happen —
 * the merchant reconnected, landed on Settings, and had to find their own way back to a panel that
 * still showed the card.
 *
 * <p><b>Why sessionStorage and not the store.</b> An off-site authorisation is a whole-page
 * navigation away and back; the app restarts and every store is empty. Session scope is also the
 * right lifetime: a note about 「I am part-way through reconnecting」 should not outlive the tab.
 *
 * <p>Reading it consumes it. A return can only be claimed once — re-posting on every subsequent
 * page load would keep re-driving tasks behind a card that closed long ago.
 */

const KEY = 'yepai.storeManager.pendingCardReturn';

export interface PendingCardReturn {
  cardId: string;
  actionId: string;
  token?: string | null;
  /**
   * [YEPAI-12 C8] Set for a Shopify re-authorisation: the scopes the grant was for. A note that
   * carries them is only settled once Shopify actually reports them granted — the grant page opens
   * in a new tab and the merchant may come back without approving, so 「came back」 alone is not
   * proof the cause is fixed. Without them the note keeps its original meaning: coming back is
   * the signal.
   */
  scopes?: string[];
  /** When it was parked, so a stale note can be ignored rather than fired days later. */
  at: number;
}

/** A reconnect that has not come back within this long is not coming back. */
const MAX_AGE_MS = 30 * 60 * 1000;

export function rememberReturn(entry: Omit<PendingCardReturn, 'at'>): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ...entry, at: Date.now() }));
  } catch {
    /* Private mode, quota, a browser that refuses: the action still works, the card just closes
       the slower way — when its tasks succeed. Never break the reconnect over bookkeeping. */
  }
}

/** Read the note without taking it. Null when there is none or it has gone stale. */
export function peekReturn(): PendingCardReturn | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingCardReturn;
    if (!parsed?.cardId || !parsed?.actionId) return null;
    if (typeof parsed.at === 'number' && Date.now() - parsed.at > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Take the note, if there is a fresh one. Always clears, so it can only be claimed once. */
export function claimReturn(): PendingCardReturn | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingCardReturn;
    if (!parsed?.cardId || !parsed?.actionId) return null;
    if (typeof parsed.at === 'number' && Date.now() - parsed.at > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Is there something waiting to be claimed? Used to decide whether to reopen the panel at all. */
export function hasPendingReturn(): boolean {
  try {
    return !!sessionStorage.getItem(KEY);
  } catch {
    return false;
  }
}
